import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '@makemypartner/shared';
import { useAuth } from '@/lib/auth';
import { Button, Card } from '@/components/ui';

function Centered({ children }: { children: ReactNode }) {
  return <div className="grid min-h-[60vh] place-items-center px-4">{children}</div>;
}

/**
 * Gate for signed-in routes.
 *
 * This is a convenience, not a security boundary. Anyone can edit the JS and
 * render the page. The actual protection is Row Level Security — the database
 * returns nothing without a valid session, so a bypassed guard shows an empty
 * screen rather than someone else's data.
 */
export default function RequireAuth({
  children,
  roles,
}: {
  children: ReactNode;
  /** If given, the profile's role must be one of these. */
  roles?: UserRole[];
}) {
  const { session, profile, loading, isSupabaseConfigured } = useAuth();
  const location = useLocation();

  if (!isSupabaseConfigured) {
    return (
      <Centered>
        <Card className="max-w-md px-6 py-10 text-center">
          <h1 className="text-xl font-semibold">Supabase is not configured</h1>
          <p className="mt-2 text-sm text-[var(--ink-2)]">
            Fill in <code className="font-[family-name:var(--font-mono)]">.env</code> and restart{' '}
            <code className="font-[family-name:var(--font-mono)]">pnpm dev</code>. See{' '}
            <code className="font-[family-name:var(--font-mono)]">docs/07-SETUP-SUPABASE.md</code>.
          </p>
        </Card>
      </Centered>
    );
  }

  // Never redirect while the first session check is in flight, or a refresh on
  // a signed-in page bounces the user to /login before the session loads.
  if (loading) {
    return (
      <Centered>
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      </Centered>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (profile?.is_banned) {
    return (
      <Centered>
        <Card className="max-w-md px-6 py-10 text-center">
          <h1 className="text-xl font-semibold">Your account is suspended</h1>
          <p className="mt-2 text-sm text-[var(--ink-2)]">
            If you think this is a mistake, contact us and we will review it.
          </p>
          <div className="mt-6">
            <Button to="/contact" variant="secondary">
              Contact support
            </Button>
          </div>
        </Card>
      </Centered>
    );
  }

  if (roles && profile && !roles.includes(profile.role)) {
    return (
      <Centered>
        <Card className="max-w-md px-6 py-10 text-center">
          <h1 className="text-xl font-semibold">Not available for your account</h1>
          <p className="mt-2 text-sm text-[var(--ink-2)]">
            This page is for {roles.join(' or ')} accounts.
          </p>
          <div className="mt-6">
            <Button to="/">Back to home</Button>
          </div>
        </Card>
      </Centered>
    );
  }

  // Profile still loading immediately after sign-in — wait rather than flash
  // the wrong-role screen.
  if (roles && !profile) {
    return (
      <Centered>
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      </Centered>
    );
  }

  return <>{children}</>;
}
